import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, RefreshCw, File, FileType, FileJson,
  Users, DollarSign, AlertTriangle, Building, ShieldAlert, MapPin
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import { fmtBs } from "./reportesUtils";

applyPlugin(jsPDF);

const CHART_COLORS = ["#7c3aed", "#a78bfa", "#c4b5fd", "#ddd6fe", "#ede9fe", "#8b5cf6", "#6d28d9", "#5b21b6"];
const PIE_COLORS = ["#ef4444", "#f59e0b", "#22c55e", "#6366f1", "#ec4899", "#14b8a6", "#f97316", "#8b5cf6"];

const MorosidadConformidad = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    rif: "", nombre: "", nro_lae: "", tipo_licencia: "", codigo_catastral: "", sector: "", estatus_solvencia: "", monto: ""
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reportes/morosidad-conformidad-uso`, { headers });
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar el reporte");
    }
    setLoading(false);
  };

  const filteredData = () => {
    if (!data) return [];
    return data.filas.filter(f => {
      if (filters.rif && !(f.rif || "").toLowerCase().includes(filters.rif.toLowerCase())) return false;
      if (filters.nombre && !(f.nombre_comercio || "").toLowerCase().includes(filters.nombre.toLowerCase())) return false;
      if (filters.nro_lae && !(f.nro_lae || "").toString().toLowerCase().includes(filters.nro_lae.toLowerCase())) return false;
      if (filters.tipo_licencia && !(f.tipo_licencia || "").toLowerCase().includes(filters.tipo_licencia.toLowerCase())) return false;
      if (filters.codigo_catastral && !(f.codigo_catastral || "").toLowerCase().includes(filters.codigo_catastral.toLowerCase())) return false;
      if (filters.sector && !(f.sector || "").toLowerCase().includes(filters.sector.toLowerCase())) return false;
      if (filters.estatus_solvencia && !(f.estatus_solvencia || "").toLowerCase().includes(filters.estatus_solvencia.toLowerCase())) return false;
      return true;
    });
  };

  const clearFilters = () => setFilters({ rif: "", nombre: "", nro_lae: "", tipo_licencia: "", codigo_catastral: "", sector: "", estatus_solvencia: "", monto: "" });

  // Exportaciones
  const exportToExcel = () => {
    const rows = filteredData().map(f => ({
      "RIF": f.rif, "Nombre Comercio": f.nombre_comercio, "N LAE": f.nro_lae,
      "Tipo Licencia": f.tipo_licencia, "Cod. Catastral": f.codigo_catastral,
      "Sector": f.sector, "Solvencia Inmob.": f.estatus_solvencia,
      "Monto Adeudado": f.monto_adeudado
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Morosidad Conformidad");
    XLSX.writeFile(wb, `morosidad_conformidad_uso_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Morosidad de Conformidad de Uso Comercial", 14, 15);
    doc.setFontSize(9);
    doc.text(`Generado: ${new Date().toLocaleDateString("es-VE")} | Total: ${filteredData().length} registros`, 14, 22);
    doc.autoTable({
      startY: 28,
      head: [["RIF", "Nombre Comercio", "N LAE", "Tipo Licencia", "Cod. Catastral", "Sector", "Solv. Inmob.", "Monto"]],
      body: filteredData().map(f => [
        f.rif, f.nombre_comercio, f.nro_lae, f.tipo_licencia,
        f.codigo_catastral, f.sector, f.estatus_solvencia,
        fmtBs(f.monto_adeudado)
      ]),
      styles: { fontSize: 7 },
      headStyles: { fillColor: [124, 58, 237] }
    });
    doc.save(`morosidad_conformidad_uso_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const exportToCSV = () => {
    const rows = filteredData();
    const header = "RIF,Nombre Comercio,N LAE,Tipo Licencia,Cod. Catastral,Sector,Solv. Inmob.,Monto Adeudado\n";
    const csv = header + rows.map(f =>
      `${f.rif},"${(f.nombre_comercio || "").replace(/"/g, '""')}",${f.nro_lae},${f.tipo_licencia},${f.codigo_catastral},"${(f.sector || "").replace(/"/g, '""')}",${f.estatus_solvencia},${f.monto_adeudado}`
    ).join("\n");
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8" }), `morosidad_conformidad_uso_${new Date().toISOString().split("T")[0]}.csv`);
  };

  const exportToJSON = () => {
    saveAs(new Blob([JSON.stringify(filteredData(), null, 2)], { type: "application/json" }), `morosidad_conformidad_uso_${new Date().toISOString().split("T")[0]}.json`);
  };

  const rows = filteredData();
  const resumen = data?.resumen || {};
  const graficos = data?.graficos || {};

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
          <p className="font-semibold text-slate-700">{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ color: p.color }}>{p.name}: {p.value}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="reporte-morosidad-conformidad">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate("/reportes")} data-testid="btn-volver-reportes">
          <ArrowLeft className="w-5 h-5 mr-2" /> Volver
        </Button>
        <div>
          <h2 className="text-2xl font-bold text-violet-900">Morosidad de Conformidad de Uso Comercial</h2>
          <p className="text-sm text-slate-500">Contribuyentes con LAE con Conformidad de Uso pendiente - {resumen.anio || new Date().getFullYear()}</p>
        </div>
      </div>

      {/* Tarjetas resumen */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Morosos</p>
                <p className="text-2xl font-bold text-violet-700">{resumen.total_morosos || 0}</p>
              </div>
              <Users className="w-8 h-8 text-violet-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Monto Adeudado</p>
                <p className="text-2xl font-bold text-red-700">{fmtBs(resumen.monto_total_adeudado)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-red-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Con Deuda Registrada</p>
                <p className="text-2xl font-bold text-amber-700">{resumen.total_con_deuda || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Sin Cod. Catastral</p>
                <p className="text-2xl font-bold text-slate-700">{resumen.total_sin_codigo || 0}</p>
              </div>
              <Building className="w-8 h-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Distribución por Tipo de Licencia - Pie */}
          {graficos.tipo_licencia?.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Distribución por Tipo de Licencia</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={graficos.tipo_licencia}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      dataKey="cantidad"
                      nameKey="nombre"
                      label={({ nombre, percent }) => `${nombre} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {graficos.tipo_licencia.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Estado Solvencia Inmobiliaria cruzado - Pie */}
          {graficos.solvencia_cruzada?.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Estado de Solvencia Inmobiliaria (cruce)</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={graficos.solvencia_cruzada}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      dataKey="cantidad"
                      nameKey="nombre"
                      label={({ nombre, cantidad }) => `${nombre}: ${cantidad}`}
                      labelLine={false}
                    >
                      {graficos.solvencia_cruzada.map((entry, i) => (
                        <Cell key={i} fill={entry.nombre === "Solvente" ? "#22c55e" : entry.nombre === "Moroso" ? "#ef4444" : "#94a3b8"} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Top sectores con más morosos - Barras */}
          {graficos.sectores?.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Sectores con Morosos</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={graficos.sectores} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="nombre" type="category" width={120} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="cantidad" name="Morosos" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Top contribuyentes con mayor monto - Barras */}
          {graficos.top_montos?.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Contribuyentes Mayor Deuda</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={graficos.top_montos} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `Bs.${(v/1000).toFixed(0)}k`} />
                    <YAxis dataKey="nombre" type="category" width={140} tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmtBs(v)} />
                    <Bar dataKey="monto" name="Monto" fill="#ef4444" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="btn-exportar-conformidad">
              <Download className="w-4 h-4" /> Exportar
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={exportToExcel} className="gap-2 cursor-pointer"><FileSpreadsheet className="w-4 h-4" /> Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToPDF} className="gap-2 cursor-pointer"><FileText className="w-4 h-4" /> PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer"><File className="w-4 h-4" /> CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer"><FileJson className="w-4 h-4" /> JSON</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" onClick={fetchData} disabled={loading} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
        <Button variant="outline" onClick={clearFilters} className="gap-2 text-slate-500">Limpiar Filtros</Button>
        <Badge variant="outline" className="ml-auto text-sm">{rows.length} de {data?.filas?.length || 0} registros</Badge>
      </div>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="tabla-morosidad-conformidad">
              <thead>
                <tr className="border-b bg-violet-50">
                  <th className="px-3 py-3 text-left text-xs font-semibold text-violet-700 uppercase">RIF</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-violet-700 uppercase">Nombre Comercio</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-violet-700 uppercase">N LAE</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-violet-700 uppercase">Tipo Licencia</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-violet-700 uppercase">Cod. Catastral</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-violet-700 uppercase">Sector</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-violet-700 uppercase">Solv. Inmob.</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-violet-700 uppercase">Monto Adeudado</th>
                </tr>
                <tr className="border-b bg-violet-50/50">
                  <th className="px-3 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.rif} onChange={e => setFilters({...filters, rif: e.target.value})} /></th>
                  <th className="px-3 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.nombre} onChange={e => setFilters({...filters, nombre: e.target.value})} /></th>
                  <th className="px-3 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.nro_lae} onChange={e => setFilters({...filters, nro_lae: e.target.value})} /></th>
                  <th className="px-3 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.tipo_licencia} onChange={e => setFilters({...filters, tipo_licencia: e.target.value})} /></th>
                  <th className="px-3 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.codigo_catastral} onChange={e => setFilters({...filters, codigo_catastral: e.target.value})} /></th>
                  <th className="px-3 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.sector} onChange={e => setFilters({...filters, sector: e.target.value})} /></th>
                  <th className="px-3 py-1"><Input className="h-7 text-xs" placeholder="Filtrar..." value={filters.estatus_solvencia} onChange={e => setFilters({...filters, estatus_solvencia: e.target.value})} /></th>
                  <th className="px-3 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="py-12 text-center text-slate-400">Cargando...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="py-12 text-center text-slate-400">No hay registros morosos</td></tr>
                ) : (
                  <>
                    {rows.map((f, i) => (
                      <tr key={i} className="border-b hover:bg-violet-50/30 transition-colors">
                        <td className="px-3 py-2.5 text-sm font-mono text-slate-700">{f.rif}</td>
                        <td className="px-3 py-2.5 text-sm text-slate-700 max-w-[200px] truncate">{f.nombre_comercio}</td>
                        <td className="px-3 py-2.5 text-sm font-mono text-slate-600">{f.nro_lae}</td>
                        <td className="px-3 py-2.5 text-sm">
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">{f.tipo_licencia}</Badge>
                        </td>
                        <td className="px-3 py-2.5 text-sm font-mono text-slate-600">
                          {f.codigo_catastral === "Sin código" ? (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Sin código</Badge>
                          ) : f.codigo_catastral || "-"}
                        </td>
                        <td className="px-3 py-2.5 text-sm text-slate-600 max-w-[150px] truncate">{f.sector}</td>
                        <td className="px-3 py-2.5 text-sm">
                          <Badge variant="outline" className={
                            f.estatus_solvencia === "Solvente" ? "bg-green-50 text-green-700 border-green-200 text-xs" :
                            f.estatus_solvencia === "Moroso" ? "bg-red-50 text-red-700 border-red-200 text-xs" :
                            "bg-slate-50 text-slate-500 border-slate-200 text-xs"
                          }>{f.estatus_solvencia}</Badge>
                        </td>
                        <td className="px-3 py-2.5 text-sm text-right font-mono">
                          {f.monto_adeudado > 0 ? (
                            <span className="text-red-600 font-semibold">{fmtBs(f.monto_adeudado)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* Fila de totales */}
                    <tr className="border-t-2 border-violet-200 bg-violet-50 font-semibold">
                      <td colSpan={7} className="px-3 py-2.5 text-sm text-violet-800">Total ({rows.length} registros)</td>
                      <td className="px-3 py-2.5 text-sm text-right font-mono text-red-700">
                        {fmtBs(rows.reduce((sum, f) => sum + f.monto_adeudado, 0))}
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MorosidadConformidad;
